import React from "react";
import { Button, Popconfirm, Table, Tag, Typography } from "antd";
import { DisconnectOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import type { Booking, Container } from "../../types/booking";
import {
  formatBookingEffReferenceFromParts,
  formatDate,
} from "../../utils/bookingUtils";
import {
  formatContainerStatusLabel,
  getContainerStatusColor,
} from "../../utils/containerStatusColors";
import {
  normalizeBookingStatus,
  statusColors,
} from "../../utils/bookingStatus";

const { Text } = Typography;

type NestedBooking = Pick<Booking, "id" | "reference" | "status"> & {
  transport_type?: number | null;
  transport_type_info?: Booking["transport_type_info"];
  company_info?:
    | { id: number; name: string }
    | { id: number; name: string }[]
    | null;
};

const getCompanyName = (booking: NestedBooking): string => {
  const company = booking.company_info;
  const single = Array.isArray(company) ? company[0] : company;
  return single?.name || "—";
};

type NestedContainer = Pick<
  Container,
  "id" | "reference" | "status" | "etd" | "eta"
> & {
  bookings?: NestedBooking[] | null;
};

interface MasterContainersNestedExpandProps {
  containers: NestedContainer[];
  masterId?: number;
  onRemoveContainer?: (masterId: number, containerId: number) => void;
  /** Path builder for booking detail links (omit for plain text) */
  getBookingPath?: (bookingId: number) => string;
  /** Path builder for container detail links (omit for plain text) */
  getContainerPath?: (containerId: number) => string;
}

export const MasterContainersNestedExpand: React.FC<
  MasterContainersNestedExpandProps
> = ({
  containers,
  masterId,
  onRemoveContainer,
  getBookingPath,
  getContainerPath,
}) => {
  const navigate = useNavigate();

  if (!containers.length) {
    return <Text type="secondary">No containers assigned</Text>;
  }

  return (
    <Table
      size="small"
      pagination={false}
      rowKey="id"
      dataSource={containers}
      className="compact-table"
      expandable={{
        rowExpandable: (container) => (container.bookings?.length || 0) > 0,
        expandedRowRender: (container) => {
          const bookings = container.bookings || [];
          if (!bookings.length) {
            return <Text type="secondary">No bookings in this container</Text>;
          }
          return (
            <Table
              size="small"
              pagination={false}
              rowKey="id"
              dataSource={bookings}
              columns={[
                {
                  title: "Booking",
                  key: "booking",
                  render: (_, booking) => {
                    const tt = booking.transport_type_info;
                    const abbrev = Array.isArray(tt)
                      ? tt[0]?.abbreviation
                      : tt?.abbreviation;
                    const label = formatBookingEffReferenceFromParts(
                      booking.id,
                      abbrev
                    );
                    if (!getBookingPath) return label;
                    return (
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0, height: "auto" }}
                        onClick={() => navigate(getBookingPath(booking.id))}
                      >
                        {label}
                      </Button>
                    );
                  },
                },
                {
                  title: "Company",
                  key: "company",
                  render: (_, booking) => getCompanyName(booking),
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (status: string | null) => {
                    const normalized = normalizeBookingStatus(status);
                    return (
                      <Tag
                        color={
                          statusColors[
                            normalized as keyof typeof statusColors
                          ] || "default"
                        }
                      >
                        {normalized}
                      </Tag>
                    );
                  },
                },
              ]}
            />
          );
        },
      }}
      columns={[
        {
          title: "Container ID",
          dataIndex: "id",
          width: 100,
        },
        {
          title: "Reference",
          dataIndex: "reference",
          render: (text: string | null, container) => {
            const label = text || "-";
            if (!getContainerPath) return label;
            return (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: "auto" }}
                onClick={() => navigate(getContainerPath(container.id))}
              >
                {label === "-" ? `Container #${container.id}` : label}
              </Button>
            );
          },
        },
        {
          title: "Status",
          dataIndex: "status",
          render: (status: string | null) => (
            <Tag color={getContainerStatusColor(status)}>
              {formatContainerStatusLabel(status)}
            </Tag>
          ),
        },
        {
          title: "Bookings",
          key: "bookings",
          width: 100,
          render: (_, container) => {
            const count = container.bookings?.length || 0;
            return (
              <Tag color={count > 0 ? "blue" : "default"}>
                {count} booking{count === 1 ? "" : "s"}
              </Tag>
            );
          },
        },
        {
          title: "ETD",
          dataIndex: "etd",
          render: (_date: string | null, container: NestedContainer) => {
            const effective = container.delayed_etd || container.etd;
            return formatDate(effective) || "—";
          },
        },
        {
          title: "ETA",
          dataIndex: "eta",
          render: (date: string | null) => formatDate(date) || "—",
        },
        ...(onRemoveContainer && masterId != null
          ? [
              {
                title: "Actions",
                key: "actions",
                render: (_: unknown, container: NestedContainer) => (
                  <Popconfirm
                    title="Remove this container from this bill of lading (B/L)?"
                    onConfirm={() =>
                      onRemoveContainer(masterId, container.id)
                    }
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="link"
                      danger
                      icon={<DisconnectOutlined />}
                      size="small"
                    >
                      Remove
                    </Button>
                  </Popconfirm>
                ),
              },
            ]
          : []),
      ]}
    />
  );
};
